// src/components/pages/Produto/modules/useCabecalhoModule.js
import { useCallback, useEffect, useRef, useState } from 'react';
// APIs
import * as DataListAPI from '../../../../api/dataList';
import * as NCMAPI from '../../../../api/NCM';
import * as SubGrupoAPI from '../../../../api/subGrupo';

/* utils */
import { formatarNumeroDecimal, formatarNumeroDecimal3Digitos, formatarOuVazio } from '../../../../utils/masks';

export function useCabecalhoModule({
    formData,
    empresaId,
    empresaTabelaId,
    isMobile,

    // infra
    setFlashMessage,

    // helpers (opcionais)
    pickSelectedId, // (value, result, field="descricao") => id
}) {
    // -------------------------
    // state principal (dados)
    // -------------------------
    const [dados, setDados] = useState(formData || {});

    // -------------------------
    // datalists / options
    // -------------------------
    const [Fabricante, setFabricante] = useState({});
    const [OptionsFabricante, setOptionsFabricante] = useState([]);

    const [NCM, setNCM] = useState({});
    const [OptionsNCM, setOptionsNCM] = useState([]);

    const [CEST, setCEST] = useState({});
    const [OptionsCEST, setOptionsCEST] = useState([]);

    const [Grupo, setGrupo] = useState({});
    const [OptionsGrupo, setOptionsGrupo] = useState([]);

    const [SubGrupo, setSubGrupo] = useState({});
    const [OptionsSubGrupo, setOptionsSubGrupo] = useState([]);
    const [subGrupoLoading, setSubGrupoLoading] = useState(false);
    const subGrupoRequestId = useRef(0);

    const [bobinaCor, setCor] = useState({});
    const [OptionsBobinaCor, setOptionsCor] = useState([]);

    const carregarSubGrupos = useCallback(
        async (grupoId) => {
            const requestId = ++subGrupoRequestId.current;

            if (!grupoId) {
                setOptionsSubGrupo([]);
                setSubGrupoLoading(false);
                return [];
            }

            setSubGrupoLoading(true);

            try {
                const response = await SubGrupoAPI.listar(grupoId);
                const lista = Array.isArray(response.data?.data)
                    ? response.data.data.filter((item) => item?.ativo === true || Number(item?.ativo) === 1)
                    : [];

                if (requestId === subGrupoRequestId.current) {
                    setOptionsSubGrupo(lista);
                }

                return lista;
            } catch (error) {
                if (requestId === subGrupoRequestId.current) {
                    setOptionsSubGrupo([]);
                    setFlashMessage(
                        error?.response?.data?.message || 'Não foi possível carregar os subgrupos.',
                        error?.response?.data?.type || 'error'
                    );
                }

                return [];
            } finally {
                if (requestId === subGrupoRequestId.current) {
                    setSubGrupoLoading(false);
                }
            }
        },
        [setFlashMessage]
    );

    useEffect(() => {
        const subGrupo = formData?.SubGrupo || formData?.subGrupo || {};

        setDados(formData || { empresaId: empresaId ?? null });
        setFabricante(formData?.Fabricante || {});
        setNCM(formData?.NCM || {});
        setCEST(formData?.CEST || {});
        setGrupo(formData?.Grupo || {});
        setSubGrupo(subGrupo);
        setCor(formData?.bobinaCor || {});

        if (formData?.grupoId) {
            carregarSubGrupos(formData.grupoId);
        } else {
            subGrupoRequestId.current += 1;
            setOptionsSubGrupo([]);
            setSubGrupoLoading(false);
        }
    }, [carregarSubGrupos, empresaId, formData]);

    //helpers
    const consultaNCMById = useCallback(
        async (id) => {
            if (!id) return;
            const result = await NCMAPI.ncmById(id);

            setNCM(result);
            setDados((prev) => ({
                ...prev,
                NCM: result,
                ncmId: id,
            }));
        },
        [setNCM, setDados]
    );
    // helper padrão datalist
    const consultaDatalist = useCallback(
        async (params) => {
            try {
                return await DataListAPI.dataList(params);
            } catch (e) {
                console.error(e);
                throw e;
            }
        },
        [DataListAPI]
    );

    const getIdByValueDom = useCallback((value) => {
        return document.querySelector(`datalist option[value="${value}"]`)?.dataset.id || null;
    }, []);

    const pickId = useCallback(
        (value, result, field = 'descricao') => {
            if (typeof pickSelectedId === 'function') return pickSelectedId(value, result, field);
            // fallback: tenta achar por field
            const found = (result || []).find((x) => String(x?.[field] || '') === String(value || ''));
            return found?.id ?? null;
        },
        [pickSelectedId]
    );

    // -------------------------
    // actions
    // -------------------------
    const setRemote = useCallback(
        (next) => {
            setDados(next || {});
            setFabricante(next?.Fabricante || {});
            setNCM(next?.NCM || {});
            setCEST(next?.CEST || {});
            setGrupo(next?.Grupo || {});
            setSubGrupo(next?.SubGrupo || next?.subGrupo || {});

            if (next?.grupoId) {
                carregarSubGrupos(next.grupoId);
            } else {
                subGrupoRequestId.current += 1;
                setOptionsSubGrupo([]);
                setSubGrupoLoading(false);
            }
        },
        [carregarSubGrupos]
    );

    const clear = useCallback(() => {
        setDados((prev) => ({
            ...prev,
            id: null,
            uuid: null,
            codigo: prev?.codigo || '',

            descricao: '',
            tipo: prev?.tipo ?? null,
            referencia: '',
            codigoFabricante: '',

            ativo: true,
            compra: false,
            venda: false,
            locavel: false,
            industria: false,
            servico: false,

            fabricanteId: null,
            ncmId: null,
            grupoId: null,
            subGrupoId: null,
            Fabricante: {},
            NCM: {},
            Grupo: {},
            SubGrupo: {},
        }));

        setFabricante({});
        setNCM({});
        setCEST({});
        setGrupo({});
        setSubGrupo({});
        setOptionsSubGrupo([]);
        subGrupoRequestId.current += 1;
        setSubGrupoLoading(false);
    }, []);

    const change = useCallback(
        async (e) => {
            const { name, value, type, checked } = e.target;
            const v = type === 'checkbox' ? !!checked : value;

            // 1) Fabricante datalist
            if (name === 'Fabricante.descricao') {
                setFabricante((prev) => ({ ...prev, descricao: v }));

                const result = await consultaDatalist({
                    empresaId: dados.empresaId,
                    tabela: 'Pessoa',
                    condicao: 'fabricante',
                    filtro: v,
                });

                setOptionsFabricante(result || []);

                const selectedId = getIdByValueDom(v) || pickId(v, result, 'descricao');

                setDados((prev) => ({
                    ...prev,
                    fabricanteId: selectedId ?? null,
                    Fabricante: selectedId ? { ...(prev.Fabricante || {}), descricao: v } : prev.Fabricante,
                }));
                return;
            }

            // 2) NCM datalist
            if (name === 'NCM.descricao') {
                const filtro = String(v ?? '').trim();
                const minimoCaracteres = isMobile ? 4 : 3;

                setNCM((prev) => ({
                    ...prev,
                    descricao: v,
                }));

                // Ao alterar o texto, a seleção anterior deixa de ser válida
                setDados((prev) => ({
                    ...prev,
                    ncmId: null,
                    NCM: {
                        ...(prev.NCM || {}),
                        descricao: v,
                    },
                }));

                if (filtro.length < minimoCaracteres) {
                    setOptionsNCM([]);
                    return;
                }

                const result = await consultaDatalist({
                    empresaId: empresaTabelaId,
                    tabela: 'NCM',
                    filtro,
                });

                setOptionsNCM(result || []);

                const selectedId = getIdByValueDom(v);

                if (selectedId) {
                    await consultaNCMById(selectedId);
                }

                return;
            }

            // 2.1) CEST datalist
            if (name === 'CEST.cest') {
                setCEST((prev) => ({ ...prev, descricao: v, cest: v }));
                const result = await consultaDatalist({
                    empresaId: empresaTabelaId,
                    tabela: 'CEST',
                    filtro: v,
                });

                setOptionsCEST(result || []);

                const selectedId = getIdByValueDom(v) || pickId(v, result, 'cest');
                setDados((prev) => ({
                    ...prev,
                    cestId: selectedId ?? null,
                    CEST: selectedId ? { ...(prev.CEST || {}), descricao: v, cest: v } : prev.CEST,
                }));
                return;
            }
            // 2.2) GRUPO datalist
            if (name === 'Grupo.descricao') {
                setGrupo((prev) => ({ ...prev, descricao: v }));
                setSubGrupo({});
                setOptionsSubGrupo([]);
                subGrupoRequestId.current += 1;
                setSubGrupoLoading(false);

                const result = await consultaDatalist({
                    empresaId: empresaTabelaId,
                    tabela: 'Grupo',
                    condicao: 'produto',
                    filtro: v,
                });

                setOptionsGrupo(result || []);

                const selectedId = pickId(v, result, 'descricao') || getIdByValueDom(v);
                const selected = (result || []).find((item) => Number(item?.id) === Number(selectedId));

                setDados((prev) => ({
                    ...prev,
                    grupoId: selectedId ?? null,
                    subGrupoId: null,
                    Grupo: selectedId ? { ...(selected || {}), descricao: v } : { descricao: v },
                    SubGrupo: {},
                }));

                if (selectedId) {
                    await carregarSubGrupos(selectedId);
                }

                return;
            }
            // 2.3) SUBGRUPO dependente do Grupo selecionado
            if (name === 'SubGrupo.descricao') {
                setSubGrupo((prev) => ({ ...prev, descricao: v }));

                const selectedId = pickId(v, OptionsSubGrupo, 'descricao');
                const selected = OptionsSubGrupo.find((item) => Number(item?.id) === Number(selectedId));

                setDados((prev) => ({
                    ...prev,
                    subGrupoId: selectedId ?? null,
                    SubGrupo: selectedId ? { ...(selected || {}), descricao: v } : { descricao: v },
                }));
                return;
            }
            // 3) Cor datalist
            if (name === 'bobinaCor.descricao') {
                setCor((prev) => ({ ...prev, descricao: v }));

                const result = await consultaDatalist({
                    empresaId: dados.empresaId,
                    tabela: 'Cor',
                    filtro: v,
                });

                setOptionsCor(result || []);

                const selectedId = getIdByValueDom(v) || pickId(v, result, 'descricao');

                setDados((prev) => ({
                    ...prev,
                    bobinaCorId: selectedId ?? null,
                    bobinaCor: selectedId ? { ...(prev.bobinaCor || {}), descricao: v } : prev.bobinaCor,
                }));
                return;
            }
            // 4) default: grava no dados (inclui Empresa, flags, inputs)
            // campos numericos
            // 1. Defina as configurações de formatação fora do handler para performance
            const fieldConfig = {
                ...Object.fromEntries(
                    ['bobinaComprimento', 'bobinaDiametro', 'estoqueMinimo'].map((f) => [f, formatarNumeroDecimal])
                ),
                ...Object.fromEntries(
                    ['pesoBruto', 'pesoLiquido', 'largura', 'altura', 'comprimento'].map((f) => [
                        f,
                        (val) => formatarOuVazio(val, formatarNumeroDecimal3Digitos),
                    ])
                ),
            };

            // 2. No seu handler:
            const formatter = fieldConfig[name];

            if (formatter) {
                const valorFormatado = formatter(name === 'pesoBruto' || name === 'pesoLiquido' ? value : v);

                setDados((prev) => ({
                    ...prev,
                    [name]: valorFormatado,
                }));
                return;
            }

            setDados((prev) => ({
                ...prev,
                [name]: v,
            }));
        },
        [
            OptionsSubGrupo,
            carregarSubGrupos,
            consultaDatalist,
            consultaNCMById,
            dados.empresaId,
            empresaTabelaId,
            getIdByValueDom,
            isMobile,
            pickId,
        ]
    );

    const blur = useCallback(async (e) => {}, []);
    const focus = useCallback(async (_e) => {}, []);

    return {
        state: {
            dados,
            setDados,
            Fabricante,
            OptionsFabricante,
            NCM,
            OptionsNCM,
            CEST,
            OptionsCEST,
            Grupo,
            OptionsGrupo,
            SubGrupo,
            OptionsSubGrupo,
            subGrupoLoading,
            bobinaCor,
            OptionsBobinaCor,
        },
        actions: {
            change,
            blur,
            focus,
            clear,
            setRemote,
            consultaDatalist,
        },
    };
}
